import React, { useEffect, useState } from 'react';
import {
  CPT_BUFFER_TIME,
  SSD_VOLUME_TABLE_HEADERS,
  MAX_ATROPS_OVERRIDE_CAPACITY_PERCENTAGE,
  MAX_ATROPS_OVERRIDE_CAPACITY_UNIT_NARROW_WINDOWS,
  SSD_FEATURE_FLAGS_LIST,
  SSD_VOLUME_SOURCES,
  OVERRIDE_VALUE_MODE,
  OVERRIDE_VALUE_MODE_KEY
} from '../../../../../../utils/constants';
import { chainWalk } from '../../../../../../utils/helpers';
import { isFeatureFlagEnabled } from '../../../../utils';
import { checkOverrideFeatureFlags } from '../../../../../../utils/AdminSettings';

// The override feature flags are fetched once and shared across the hooks below, so a view that
// uses several of them (units / toggle / narrow-windows) issues a single network call instead of
// one fetch per hook. checkOverrideFeatureFlags never rejects (it returns defaults on error), so
// caching the promise is safe.
let overrideFeatureFlagsPromise = null;
const getOverrideFeatureFlags = () => {
  if (!overrideFeatureFlagsPromise) {
    overrideFeatureFlagsPromise = checkOverrideFeatureFlags();
  }
  return overrideFeatureFlagsPromise;
};

// Sources that contribute to a planning charge post-plan-creation.
export const SSD_PLANNING_CHARGE_VOLUME_SOURCES = [
  SSD_VOLUME_SOURCES.ATROPS,
  SSD_VOLUME_SOURCES.AFT_CHARGE,
  SSD_VOLUME_SOURCES.DDP_FORECAST,
  SSD_VOLUME_SOURCES.SNOP,
  'Override - PercentATROPS'
];

export const useUnitsOverrideUiEnabled = () => {
  const [isUnitsOverrideUiEnabled, setIsUnitsOverrideUiEnabled] = useState(false);
  useEffect(() => {
    getOverrideFeatureFlags().then((flags) => {
      setIsUnitsOverrideUiEnabled(flags.isAtropsVolumeUnitsOverrideEnabled);
    });
  }, []);
  return isUnitsOverrideUiEnabled;
};

// Gates the per-override value-mode (% vs absolute unit) toggle in the override popovers.
// Backed by the isOverrideModeToggleEnabled AppConfig feature flag (separate from the
// isAtropsVolumeCapacityUnitsOverrideEnabled units-override flag).
export const useOverrideModeToggleEnabled = () => {
  const [isOverrideModeToggleEnabled, setIsOverrideModeToggleEnabled] = useState(false);
  useEffect(() => {
    getOverrideFeatureFlags().then((flags) => {
      setIsOverrideModeToggleEnabled(flags.isOverrideModeToggleEnabled);
    });
  }, []);
  return isOverrideModeToggleEnabled;
};

// Narrow Windows feature flag. Drives the default override mode (NW sites default to absolute unit).
export const useNarrowWindowsEnabled = () => {
  const [isNarrowWindowsEnabled, setIsNarrowWindowsEnabled] = useState(false);
  useEffect(() => {
    getOverrideFeatureFlags().then((flags) => {
      setIsNarrowWindowsEnabled(flags.isNarrowWindowsEnabled);
    });
  }, []);
  return isNarrowWindowsEnabled;
};

// Initial per-override mode:
//   1. the mode the override was saved in (maintained across carries/redisplay); else
//   2. for a legacy override that has a value but no saved mode, the interpretation it was authored
//      under (the global units flag) — so opening/re-saving it never silently flips % <-> units; else
//   3. for a brand-new override, the default implied by the Narrow Windows flag (NW -> absolute unit).
export const initialOverrideMode = ({
  storedValueMode,
  hasStoredValue,
  isNarrowWindowsEnabled,
  isUnitsOverrideUiEnabled
}) => {
  if (storedValueMode) {
    return storedValueMode;
  }
  if (hasStoredValue) {
    return isUnitsOverrideUiEnabled ? OVERRIDE_VALUE_MODE.ABSOLUTE_UNIT : OVERRIDE_VALUE_MODE.PERCENTAGE;
  }
  return isNarrowWindowsEnabled ? OVERRIDE_VALUE_MODE.ABSOLUTE_UNIT : OVERRIDE_VALUE_MODE.PERCENTAGE;
};

// Whether the override value is interpreted as absolute units.
// - When the per-override toggle is enabled, the legacy units feature flag is ignored entirely and
//   the (stored or planner-selected) per-override mode decides.
// - When the toggle is disabled, a previously-stored mode is still honored (so a mode set earlier is
//   maintained across carries/redisplay); otherwise fall back to the legacy units feature flag.
export const resolveIsUnitsMode = (
  isOverrideModeToggleEnabled,
  overrideMode,
  storedValueMode,
  isUnitsOverrideUiEnabled
) => {
  if (isOverrideModeToggleEnabled) {
    return overrideMode === OVERRIDE_VALUE_MODE.ABSOLUTE_UNIT;
  }
  if (storedValueMode) {
    return storedValueMode === OVERRIDE_VALUE_MODE.ABSOLUTE_UNIT;
  }
  return isUnitsOverrideUiEnabled;
};

// Reusable per-override mode state shared by every override popover (Pick2Rebin, Grocery).
export const useOverrideMode = ({
  storedValueMode,
  hasStoredValue,
  isNarrowWindowsEnabled,
  isOverrideModeToggleEnabled,
  isUnitsOverrideUiEnabled,
  resetValue
}) => {
  const [overrideMode, setOverrideMode] = useState(() =>
    initialOverrideMode({ storedValueMode, hasStoredValue, isNarrowWindowsEnabled, isUnitsOverrideUiEnabled })
  );
  const [userChangedMode, setUserChangedMode] = useState(false);
  // The Narrow Windows / units flags arrive asynchronously (after this component first renders), so
  // the useState initializer above can capture a pre-load default. Re-sync once the flags resolve,
  // unless the override already has a stored mode or the planner has already picked one.
  useEffect(() => {
    if (!storedValueMode && !userChangedMode) {
      setOverrideMode(
        initialOverrideMode({ storedValueMode, hasStoredValue, isNarrowWindowsEnabled, isUnitsOverrideUiEnabled })
      );
    }
  }, [isNarrowWindowsEnabled, isUnitsOverrideUiEnabled, storedValueMode, hasStoredValue, userChangedMode]);
  const isUnitsMode = resolveIsUnitsMode(
    isOverrideModeToggleEnabled,
    overrideMode,
    storedValueMode,
    isUnitsOverrideUiEnabled
  );
  // Switching the mode always clears the entered value: the same raw number means different things
  // in percentage vs absolute-unit mode, so it must be re-entered to avoid silent misinterpretation.
  const onModeChange = (nextMode) => {
    setUserChangedMode(true);
    setOverrideMode(nextMode);
    if (resetValue) {
      resetValue('');
    }
  };
  return { overrideMode, setOverrideMode, isUnitsMode, onModeChange };
};

// Interpret a stored override's key-value map using its per-override value mode when present,
// falling back to the global units flag for legacy overrides. Shared by every view-layer volume
// calculator so the interpretation cannot drift between them (a % override read as absolute units
// collapses the planning charge to 0).
export const resolveStoredIsUnitsMode = (overrideKeyValueMap, isUnitsOverrideUiEnabled) => {
  const storedValueMode = overrideKeyValueMap?.[OVERRIDE_VALUE_MODE_KEY];
  return storedValueMode != null ? storedValueMode === OVERRIDE_VALUE_MODE.ABSOLUTE_UNIT : isUnitsOverrideUiEnabled;
};

// A volume/capacity override value is valid when present and within [0, maxOverride].
export const isOverrideValueValid = (value, maxOverride) =>
  value != null && value !== '' && 0 <= value && value <= maxOverride;

export const getMaxAtropsOverrideCapacityPercentage = (adminSettings) => {
  const isNarrowWindowsEnabled = isFeatureFlagEnabled(adminSettings, SSD_FEATURE_FLAGS_LIST.narrowWindowsEnabled);
  return isNarrowWindowsEnabled
    ? MAX_ATROPS_OVERRIDE_CAPACITY_UNIT_NARROW_WINDOWS
    : MAX_ATROPS_OVERRIDE_CAPACITY_PERCENTAGE;
};

const isEquivalentCpt = (demandCpt, otherCpt) => {
  // Convert timestamp strings to Date objects
  const demandTimeSeconds = Math.floor(new Date(demandCpt).getTime() / 1000);
  const otherTimeSeconds = Math.floor(new Date(otherCpt).getTime() / 1000);

  // Compare the difference in seconds
  return Math.abs(demandTimeSeconds - otherTimeSeconds) <= CPT_BUFFER_TIME;
};

/*
 * Calculates the total volume from demand data by filtering based on CPT datetime, process paths, roles, and volume sources,
 * then sums the volume values of matching entries.
 */
export const findTotalVolumeFromDemand = (demand, cptDateTimeLocal, processPaths, roles, sources) => {
  return demand
    .filter(
      ({ cpt_local, process_path, process_role, volume_source }) =>
        isEquivalentCpt(cpt_local, cptDateTimeLocal) &&
        (processPaths.length === 0 || processPaths.indexOf(process_path) >= 0) &&
        (roles.length === 0 || roles.indexOf(process_role) >= 0) &&
        (sources.length === 0 ||
          sources.filter((sourceName) => chainWalk(() => volume_source.startsWith(sourceName), false)).length > 0)
    )
    .reduce((sum, { volume }) => sum + volume, 0);
};

export const getTableHeaders = (featureFlags) => {
  const { isGrocery, isDdpEnabled } = featureFlags;
  return isGrocery
    ? [
        {
          cells: [
            { content: null },
            { content: null, width: 300 },
            { content: SSD_VOLUME_TABLE_HEADERS.PLANNING_CHARGE, backgroundColor: 'secondary' },
            { content: SSD_VOLUME_TABLE_HEADERS.RODEO_BACKLOG, backgroundColor: 'secondary' }
          ]
        }
      ]
    : [
        {
          cells: [
            { content: null },
            { content: null, width: 300 },
            { content: <strong>Sortable (Pick2Rebin)</strong>, colSpan: isDdpEnabled ? 2 : 3 }
          ]
        },
        {
          cells: [
            { content: null },
            { content: null, width: 300 },
            { content: SSD_VOLUME_TABLE_HEADERS.PREDICTED_REMAINING_CHARGE, hidden: isDdpEnabled },
            { content: SSD_VOLUME_TABLE_HEADERS.MAX_POTENTIAL_CHARGE, backgroundColor: 'secondary' },
            { content: SSD_VOLUME_TABLE_HEADERS.PLANNING_CHARGE, backgroundColor: 'secondary' },
            { content: SSD_VOLUME_TABLE_HEADERS.VNA_PREDICTED_REMAINING_CHARGE, hidden: isDdpEnabled },
            { content: SSD_VOLUME_TABLE_HEADERS.RODEO_BACKLOG },
            { content: SSD_VOLUME_TABLE_HEADERS.TOTAL_PLANNING_VOLUME, hidden: isDdpEnabled }
          ]
        }
      ];
};

export const getPredictedChargeValueByRoleName = (outboundVolume, roleName) => {
  const predictedCharge = outboundVolume?.predictedChargeList.find((item) => item.role.name?.includes(roleName));

  return predictedCharge?.chargeValue || 0;
};

export const getBacklogChargeValueByRoleName = (outboundVolume, roleName) => {
  const getPickChargeValue =
    outboundVolume?.backlogList.find((item) => {
      return item.role.process === 'pick' && item.role.name?.includes(roleName);
    })?.chargeValue || 0;

  const getPackChargeValue =
    outboundVolume?.backlogList.find((item) => {
      return item.role.process === 'pack' && item.role.name?.includes(roleName);
    })?.chargeValue || 0;

  return getPickChargeValue + getPackChargeValue;
};

export const getCapacity = (outboundVolume) => {
  return outboundVolume.capacity || 0;
};

export const getScheduled = (outboundVolume) => {
  return outboundVolume.schedule || 0;
};
